import clsx from 'clsx';
import { HTMLAttributes, forwardRef } from 'react';

type Variant =
  | 'H1'
  | 'H1-med'
  | 'H1-med-resp'
  | 'H1-med'
  | 'H1-med-resp'
  | 'H2'
  | 'H3'
  | 'H3-med'
  | 'H3-med-resp'
  | 'H3-resp'
  | 'H4'
  | 'H4-med'
  | 'H5'
  | 'p1'
  | 'p1-resp'
  | 'p2'
  | 'p2-semi'
  | 'p2-med'
  | 'p3'
  | 'p3-reg'
  | 'p3-resp'
  | 'p3-reg'
  | 'p3-resp'
  | 'buttons-1'
  | 'buttons-2'
  | 'buttons-m'
  | 'base'
  | 'caption'
  | 'buttons-m'
  | 'base'
  | 'caption'
  | 'product-2'
  | 'toast';

type FontWeight = 300 | 400 | 500 | 600 | 700 | 800;

type CustomProps = {
  variant?: Variant;
  fontWeight?: FontWeight;
  color?: string;
  truncate?: boolean;
};

interface ParagraphProps
  extends
    React.DetailedHTMLProps<HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
    CustomProps {}

const Typography = forwardRef<HTMLParagraphElement, ParagraphProps>(
  (
    {
      className,
      children,
      variant = 'p1',
      color = 'text-primary-900',
      truncate,
      fontWeight,
      ...rest
    },
    ref
  ) => {
    return (
      <p
        ref={ref}
        className={clsx(
          variantStyleMap[variant],
          fontWeight ? fontWeightStyleMap[fontWeight] : undefined,
          { 'overflow-hidden text-ellipsis max-w-full whitespace-nowrap': truncate },
          color,
          className
        )}
        {...rest}
      >
        {children}
      </p>
    );
  }
);
Typography.displayName = 'Typography';

export default Typography;

const variantStyleMap: { [k in Variant]: string } = {
  H1: 'text-[60px] leading-[70px]',
  'H1-med': 'text-[32px] leading-[30px]',
  'H1-med-resp':
    'text-[18px] leading-[18px] 2xl:text-[24px] 2xl:leading-[24px]  3xl:text-[32px] 3xl:leading-[32px]',
  H2: 'text-[30px] leading-[30px]',
  H3: 'text-[24px] leading-[24px]',
  'H3-med': 'text-[24px] leading-[24px] font-medium',
  'H3-resp': 'text-[18px] leading-[24px] 4xl:text-[24px] 4xl:leading-[24px]',
  'H3-med-resp':
    'text-[21px] xl:text-[24px] leading-[24px] 2xl:text-[28px]  2xl:leading-[30px] 3xl:text-[30px] font-medium',
  H4: 'text-[16px] leading-[24px] 2xl:text-[20px] 2xl:leading-[24px]',
  'H4-med': 'text-[16px] leading-[24px] 2xl:text-[18px] 2xl:leading-[24px]',
  H5: 'text-[16px] leading-[24px] 2xl:text-[18px] 2xl:leading-[24px] font-medium',
  p1: 'text-[16px] leading-[20px] 2xl:text-[18px] 2xl:leading-[20px]',
  'p1-resp': 'text-[16px] leading-[20px] 4xl:text-[18px] 4xl:leading-[20px]',
  p2: 'text-[14px] leading-[16px] 2xl:text-[16px] 2xl:leading-[24px]',
  'p2-semi': 'text-[16px] leading-[24px]',
  'p2-med': 'text-[18px] leading-[24px]',
  p3: 'text-[14px] leading-[20px] font-medium',
  'p3-reg': 'text-[14px] leading-[20px]',
  'p3-resp': 'text-[12px] leading-[20px] 2xl:text-[14px] 2xl:leading-[20px]',
  'buttons-1': 'text-[18px] leading-[22px] font-semibold',
  'buttons-2': 'text-[12px] leading-[16px]',
  'buttons-m': 'text-[14px] leading-[20px]',
  // 'buttons-m': 'text-[12px] leading-[16px]',
  base: 'text-[14px] leading-[19.6px] font-medium',
  caption: 'text-[12px] leading-[16px]',
  'product-2': 'text-[14px] leading-[24px]',
  toast: 'text-[16px] leading-[20px] 2xl:text-[18px] 2xl:leading-[24px] font-medium',
};

const fontWeightStyleMap: { [k in FontWeight]: string } = {
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
  800: 'font-extrabold',
};
